import { useToasts } from '@/common/composables/useToast';
import moment from 'moment';
import { defineStore } from 'pinia';
import { computed, ref } from 'vue';
import { getCancellationStatusService } from '../services/cancellation.service';
import type { ComprobanteBasicInfo } from '../types/cancellation.types';

export const useCancellationPendingStore = defineStore('cancellationPendingStore', () => {
  // State
  const pendingComprobanteIds = ref<number[]>([]);
  const pollIntervals = ref<Record<number, ReturnType<typeof setTimeout>>>({});
  const consecutiveErrors = ref<Record<number, number>>({});

  // Composables
  const { errorToast, successToast } = useToasts();

  // Custom Callbacks to be provided by UI for generic/global banner use cases
  // But to keep it simple, we can rely on passing callbacks or global emitters.
  const bannerComprobanteFailed = ref<ComprobanteBasicInfo | null>(null);

  // Triggers for global query invalidation (to be observed via composable)
  const latestSuccessComprobante = ref<ComprobanteBasicInfo | null>(null);
  const latestFailedComprobante = ref<ComprobanteBasicInfo | null>(null);

  // Getters
  const pendingCount = computed(() => pendingComprobanteIds.value.length);
  const isPending = computed(() => (comprobanteId: number) => {
    return pendingComprobanteIds.value.includes(comprobanteId);
  });

  // Actions
  const syncToSessionStorage = () => {
    try {
      const data = {
        ids: pendingComprobanteIds.value,
        timestamp: new Date().toISOString(),
      };
      sessionStorage.setItem('munzen_pending_cancellations', JSON.stringify(data));
    } catch (e) {
      console.error('Failed to sync pending cancellations to sessionStorage', e);
    }
  };

  const loadFromSessionStorage = () => {
    try {
      const dataStr = sessionStorage.getItem('munzen_pending_cancellations');
      if (dataStr) {
        const data = JSON.parse(dataStr);
        if (Array.isArray(data.ids)) {
          // Verify age or just use them
          pendingComprobanteIds.value = data.ids;
        }
      }
    } catch (e) {
      console.error('Failed to load pending cancellations from sessionStorage', e);
    }
  };

  const addPending = (comprobanteId: number) => {
    if (!pendingComprobanteIds.value.includes(comprobanteId)) {
      pendingComprobanteIds.value.push(comprobanteId);
      syncToSessionStorage();
    }
  };

  const removePending = (comprobanteId: number) => {
    pendingComprobanteIds.value = pendingComprobanteIds.value.filter(id => id !== comprobanteId);
    syncToSessionStorage();
  };

  /**
   * @description Resume polling for all pending cancellations after page reload
   * This is intended to be called in onMounted of main list/detail views
   */
  const initializePollingFromStorage = () => {
    loadFromSessionStorage();
    resumePolling();
  };

  const stopPolling = (comprobanteId: number) => {
    if (pollIntervals.value[comprobanteId]) {
      clearTimeout(pollIntervals.value[comprobanteId]);
      delete pollIntervals.value[comprobanteId];
    }
    delete consecutiveErrors.value[comprobanteId];
  };

  const stopAllPolling = () => {
    Object.keys(pollIntervals.value).forEach(id => {
      clearTimeout(pollIntervals.value[Number(id)]);
    });
    pollIntervals.value = {};
    consecutiveErrors.value = {};
  };

  const _defaultOnSuccess = (comprobante: ComprobanteBasicInfo) => {
    latestSuccessComprobante.value = comprobante;
    successToast(
      'Cancelación Exitosa',
      `¡Factura ${comprobante.serie}-${comprobante.folio || ''} de ${comprobante.receptor_razon_social} fue cancelada exitosamente!`,
      'top-right',
      20000,
    );
  };

  const _defaultOnFailed = (comprobante: ComprobanteBasicInfo) => {
    latestFailedComprobante.value = comprobante;
    bannerComprobanteFailed.value = comprobante;
  };

  const startPolling = (
    comprobanteId: number,
    onSuccess?: (comprobante: ComprobanteBasicInfo) => void,
    onFailed?: (comprobante: ComprobanteBasicInfo) => void,
  ) => {
    // Deduplication check
    if (pollIntervals.value[comprobanteId]) {
      return; // Already polling
    }

    consecutiveErrors.value[comprobanteId] = 0;

    const scheduleNextPoll = (delay: number = 60000) => {
      if (pollIntervals.value[comprobanteId]) {
        clearTimeout(pollIntervals.value[comprobanteId]);
      }

      const timeout = setTimeout(async () => {
        try {
          const response = await getCancellationStatusService(comprobanteId);
          // Reset errors on success
          consecutiveErrors.value[comprobanteId] = 0;

          if (!response.has_pending_cancellation) {
            stopPolling(comprobanteId);
            removePending(comprobanteId);

            if (response.status === 'completed') {
              // Always trigger global notification and state updates for async completion
              _defaultOnSuccess(response.comprobante);
              // Also call the optional local callback if provided
              if (onSuccess) onSuccess(response.comprobante);
            } else if (response.status === 'failed') {
              // Always trigger global notification and state updates for async failure
              _defaultOnFailed(response.comprobante);
              // Also call the optional local callback if provided
              if (onFailed) onFailed(response.comprobante);
            }
          } else {
            // Still pending, calculate next delay
            let nextDelay = 60000;
            if (response.next_retry_at) {
              const retryTime = moment(response.next_retry_at);
              if (retryTime.isValid()) {
                const delta = retryTime.diff(moment());
                // Se añaden 20 segundos de gracia. Esto evita las "carreras" (race conditions) donde consultamos
                // en el milisegundo exacto y obtenemos la respuesta anterior, además cubre drifts de reloj del cliente.
                // Si delta <= 0 (ya pasó o es exacto), se fuerza un delay mínimo de 20s.
                nextDelay = Math.max(20000, delta + 20000);
              }
            }
            scheduleNextPoll(nextDelay);
          }
        } catch (error) {
          const currentErrors = (consecutiveErrors.value[comprobanteId] || 0) + 1;
          consecutiveErrors.value[comprobanteId] = currentErrors;

          console.error(`Polling error for invoice ${comprobanteId}:`, error);

          if (currentErrors >= 10) {
            stopPolling(comprobanteId);
            errorToast(
              'Error de conexión',
              'No se pudo verificar el estado de la cancelación. Por favor, recarga la página.',
            );
          } else {
            // Error occurred, backoff and retry in 60 seconds
            scheduleNextPoll(60000);
          }
        }
      }, delay);

      pollIntervals.value[comprobanteId] = timeout;
    };

    scheduleNextPoll(60000); // Start the first poll
  };

  const resumePolling = (
    onSuccess?: (comprobante: ComprobanteBasicInfo) => void,
    onFailed?: (comprobante: ComprobanteBasicInfo) => void,
  ) => {
    pendingComprobanteIds.value.forEach(id => {
      startPolling(id, onSuccess, onFailed);
    });
  };

  const $reset = () => {
    stopAllPolling();
    pendingComprobanteIds.value = [];
    bannerComprobanteFailed.value = null;
    latestSuccessComprobante.value = null;
    latestFailedComprobante.value = null;
    try {
      sessionStorage.removeItem('munzen_pending_cancellations');
    } catch {
      // Ignored
    }
  };

  return {
    pendingComprobanteIds,
    pollIntervals,
    consecutiveErrors,
    bannerComprobanteFailed,
    latestSuccessComprobante,
    latestFailedComprobante,
    pendingCount,
    isPending,
    addPending,
    removePending,
    startPolling,
    stopPolling,
    stopAllPolling,
    resumePolling,
    initializePollingFromStorage,
    syncToSessionStorage,
    loadFromSessionStorage,
    $reset,
  };
});
